import React from "react";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PosCard } from "./PosCard";

interface DraggableItem {
    id: string;
    active: boolean;
    isFavorite: boolean;
}

/**
 * Pure helper for the drag-end reorder. Returns a new list with the
 * dragged item moved to the drop index, or null when the drop is a
 * no-op (same item, unknown ids, empty input) so the caller can skip
 * dispatching an update.
 */
export function computeReorder<T extends DraggableItem>(
    items: T[],
    activeId: string,
    overId: string | null
): T[] | null {
    if (!overId || activeId === overId) return null;
    const srcIndex = items.findIndex((it) => it.id === activeId);
    const destIndex = items.findIndex((it) => it.id === overId);
    if (srcIndex < 0 || destIndex < 0) return null;
    const next = items.map((it) => ({ ...it }));
    const [moved] = next.splice(srcIndex, 1);
    next.splice(destIndex, 0, moved);
    return next;
}

interface DraggableCardListProps<T extends DraggableItem> {
    droppableId: string;
    items: T[];
    getTitle: (item: T) => string;
    getSubtitle?: (item: T) => string | undefined;
    getIcon?: (item: T) => React.ReactNode | undefined;
    isToggleDisabled?: (item: T, activeCount: number) => boolean;
    isGreyedOut?: (item: T) => boolean;
    onReorder: (items: T[]) => void;
    onToggle: (index: number) => void;
    testIdPrefix: string;
}

interface SortableCardProps<T extends DraggableItem> {
    item: T;
    index: number;
    title: string;
    subtitle: string | undefined;
    icon: React.ReactNode | undefined;
    disabled: boolean;
    greyedOut: boolean;
    onToggle: (index: number) => void;
    testIdPrefix: string;
}

function SortableCard<T extends DraggableItem>({
    item,
    index,
    title,
    subtitle,
    icon,
    disabled,
    greyedOut,
    onToggle,
    testIdPrefix,
}: SortableCardProps<T>) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            aria-label={title}
        >
            <PosCard
                title={title}
                subtitle={subtitle}
                active={item.active}
                isFavorite={item.isFavorite}
                disabled={disabled}
                greyedOut={greyedOut}
                icon={icon}
                onToggle={() => onToggle(index)}
                data-testid={`pos-${testIdPrefix}-card-${item.id}`}
            />
        </div>
    );
}

export function DraggableCardList<T extends DraggableItem>({
    droppableId,
    items,
    getTitle,
    getSubtitle,
    getIcon,
    isToggleDisabled,
    isGreyedOut,
    onReorder,
    onToggle,
    testIdPrefix,
}: DraggableCardListProps<T>) {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const itemIds = React.useMemo(() => items.map((it) => it.id), [items]);

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            const next = computeReorder(
                items,
                active.id as string,
                (over?.id as string) || null
            );
            if (next) onReorder(next);
        },
        [items, onReorder]
    );

    const activeCount = React.useMemo(
        () => items.filter((it) => it.active).length,
        [items]
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={itemIds}
                strategy={horizontalListSortingStrategy}
            >
                <div
                    className="pos-cards-container__clz h-100"
                    data-testid={`pos-${testIdPrefix}-drag-area`}
                    id={droppableId}
                >
                    {items.map((item, index) => {
                        const disabled = isToggleDisabled
                            ? isToggleDisabled(item, activeCount)
                            : item.isFavorite && item.active;
                        const greyedOut = isGreyedOut
                            ? isGreyedOut(item)
                            : false;
                        return (
                            <SortableCard
                                key={item.id}
                                item={item}
                                index={index}
                                title={getTitle(item)}
                                subtitle={getSubtitle?.(item)}
                                icon={getIcon?.(item)}
                                disabled={disabled}
                                greyedOut={greyedOut}
                                onToggle={onToggle}
                                testIdPrefix={testIdPrefix}
                            />
                        );
                    })}
                </div>
            </SortableContext>
        </DndContext>
    );
}
